import React from 'react';
import { Handle, Position } from 'reactflow';
import { AlertTriangle, Plus } from 'lucide-react';
import { kindMeta, verdictAccent, verdictIcon } from './nodeKinds';
import { NODE_W } from './graphModel';

/**
 * A node on the canvas: its mark, its name, and — after a test — what it did.
 *
 * Not draggable: the layout is computed from the graph. Click it for the
 * panel; press + below it to insert the next step on its default edge.
 */
export function NodeMark({ kind, node, size = 26 }) {
  const meta = kindMeta(kind);
  const Icon = kind === 'verdict' ? verdictIcon(node) : meta.Icon;
  const accent = kind === 'verdict' ? verdictAccent(node) : meta.accent;
  return (
    <span title={meta.label} style={{ width: size, height: size, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: `color-mix(in srgb, ${accent} 13%, transparent)`, color: accent }}>
      <Icon style={{ width: size * 0.55, height: size * 0.55 }} strokeWidth={1.9} />
    </span>
  );
}

const STATE_COLOR = {
  ok: 'var(--feedback-success, #16a34a)', errored: 'var(--feedback-error, #dc2626)', pending: '#d97706', not_ready: '#d97706', skipped: 'var(--text-muted)', resolved: 'var(--feedback-success, #16a34a)',
};

/** What starts the eval, above its first step: "Every run of support-bot". Not part of the graph. */
export function TriggerNode({ data }) {
  return (
    <div className="eval3-trigger" title="Click for the eval's settings: what it applies to"
      style={{
        width: NODE_W, boxSizing: 'border-box', borderRadius: 999, border: '1px dashed var(--border-strong)', padding: '7px 12px',
        textAlign: 'center', fontFamily: 'Inter, sans-serif', fontSize: 12, color: 'var(--text-secondary)', background: 'var(--surface-sunken)', cursor: 'pointer',
      }}>
      {data?.name}
      <Handle type="source" position={Position.Bottom} isConnectable={false} style={{ background: 'var(--border-default)', width: 8, height: 8, border: 'none' }} />
    </div>
  );
}

export default function EvalNode({ id, data, selected }) {
  const { kind, node, name, invalid, invalidMsg, step, isEntry, readOnly, outcomes: outs = [] } = data || {};
  const meta = kindMeta(kind);
  const accent = kind === 'verdict' ? verdictAccent(node) : meta.accent;
  const stepColor = step ? STATE_COLOR[step.state] || null : null;
  const borderColor = stepColor || (invalid ? 'var(--feedback-error)' : selected ? 'var(--text-primary)' : `color-mix(in srgb, ${accent} 26%, transparent)`);
  const verdict = kind === 'verdict';
  const summary = step && step.state === 'ok' && step.value != null
    ? Object.entries(typeof step.value === 'object' ? step.value : { value: step.value }).map(([k, v]) => `${k}: ${typeof v === 'number' ? Number(v.toFixed(3)) : String(v)}`).join(' · ')
    : step?.state === 'errored' ? (step.reason || 'error') : step?.state === 'pending' ? 'waiting for a person' : step?.state === 'not_ready' ? 'sandbox warming up' : step?.state === 'skipped' ? 'skipped: no spans matched' : null;

  return (
    <div
      className="eval3-node"
      title={meta.blurb}
      style={{
        position: 'relative', width: NODE_W, boxSizing: 'border-box', background: 'var(--surface-base)',
        border: `${selected || invalid || stepColor ? 2 : 1}px solid ${borderColor}`, borderRadius: 12, padding: '10px 12px',
        fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: 'var(--text-primary)',
        boxShadow: selected ? '0 0 0 3px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.06)', transition: 'border-color 0.2s', cursor: 'pointer',
      }}
    >
      {invalid && (
        <span title={invalidMsg || 'Not set up yet'} style={{ position: 'absolute', top: -8, right: -8, width: 18, height: 18, borderRadius: '50%', background: 'var(--surface-base)', border: '1px solid rgba(239,68,68,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AlertTriangle style={{ width: 11, height: 11, color: 'var(--feedback-error)' }} />
        </span>
      )}
      {!isEntry && <Handle type="target" position={Position.Top} style={{ background: 'var(--border-default)', width: 8, height: 8, border: 'none' }} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
        <NodeMark kind={kind} node={node} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || meta.label}</div>
          {/* Only what the name does not already say: a verdict's side, or what
              the last test got. The kind is the mark; the output's field name
              is machinery nobody here needs to see. */}
          {(summary || verdict) && (
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {summary || (node?.config?.passed === false ? `fail${node?.config?.reason_code ? ` · ${node.config.reason_code}` : ''}` : 'pass')}
            </div>
          )}
        </div>
      </div>
      {/*
        One connection point per OUTCOME, spread along the bottom edge and
        named under the dot. A step that answers three ways has three dots, so
        you can drag "unsure" somewhere without opening the panel and without
        guessing which line you grabbed. A step whose routing the panel cannot
        express as a list keeps the single anonymous handle.
      */}
      {!verdict && (outs.length > 1 ? outs.map((o, i) => {
        const left = `${((i + 1) / (outs.length + 1)) * 100}%`;
        return (
          <React.Fragment key={o.key}>
            {/* Hollow until it leads somewhere, so what is still unwired is visible
                from across the canvas rather than only in the panel. */}
            <Handle id={o.key} type="source" position={Position.Bottom} title={o.to ? o.label : `${o.label}: not connected`}
              style={{
                left, width: 9, height: 9,
                background: o.to ? accent : 'var(--surface-base)',
                border: o.to ? '2px solid var(--surface-base)' : `2px dashed ${accent}`,
              }} />
            <span style={{
              position: 'absolute', left, bottom: -15, transform: 'translateX(-50%)', pointerEvents: 'none',
              maxWidth: NODE_W / outs.length - 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontSize: 9, lineHeight: '10px', color: 'var(--text-muted)',
            }}>{o.label}</span>
          </React.Fragment>
        );
      }) : <Handle type="source" position={Position.Bottom} style={{ background: outs[0] && !outs[0].to ? 'var(--surface-base)' : 'var(--border-default)', border: outs[0] && !outs[0].to ? `2px dashed ${accent}` : 'none', width: outs[0] && !outs[0].to ? 9 : 8, height: outs[0] && !outs[0].to ? 9 : 8 }} />)}
      {!verdict && !readOnly && outs.length <= 1 && (
        <button type="button" className="eval3-node-add" title="Insert a step after this one"
          onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('eval3:add-after', { detail: { nodeId: id } })); }}
          style={{ position: 'absolute', left: '50%', bottom: -14, transform: 'translateX(-50%)', width: 22, height: 22, borderRadius: '50%', border: '1px solid var(--border-default)', background: 'var(--surface-base)', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: 0, transition: 'opacity 0.15s', zIndex: 5 }}>
          <Plus style={{ width: 12, height: 12 }} />
        </button>
      )}
    </div>
  );
}
